import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainSidebar from 'components/MainSidebar';
import Icon from 'components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const JailPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [playerStats, setPlayerStats] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [bailing, setBailing] = useState(false);
  const [bailError, setBailError] = useState('');
  const [error, setError] = useState(null);

  const fetchJailStatus = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: statsErr } = await supabase
        ?.from('player_stats')
        ?.select('jail_until, bail_cost, evidence_level, cash, level, energy, max_energy, xp, xp_to_next_level')
        ?.eq('user_id', user?.id)
        ?.single();
      if (statsErr && statsErr?.code !== 'PGRST116') throw statsErr;
      setPlayerStats(data || null);

      if (data?.jail_until) {
        const remaining = Math.max(0, Math.round((new Date(data?.jail_until) - new Date()) / 1000));
        setTimeRemaining(remaining);
        // If not actually jailed, redirect to dashboard
        if (remaining <= 0) {
          navigate('/dashboard');
        }
      } else {
        // Not jailed, redirect
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Jail fetch error:', err);
      setError(err?.message || 'Failed to load jail status');
    } finally {
      setLoading(false);
    }
  }, [user?.id, navigate]);

  useEffect(() => { fetchJailStatus(); }, [fetchJailStatus]);

  useEffect(() => {
    if (timeRemaining <= 0) return;
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/dashboard');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timeRemaining, navigate]);

  const formatTime = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${String(h)?.padStart(2,'0')}:${String(m)?.padStart(2,'0')}:${String(s)?.padStart(2,'0')}`;
  };

  const handleBailOut = async () => {
    if (!user?.id) return;
    const bailCost = parseFloat(playerStats?.bail_cost || 0);
    const cash = parseFloat(playerStats?.cash || 0);
    if (cash < bailCost) {
      setBailError(`Insufficient funds. You need $${bailCost?.toLocaleString()} but only have $${cash?.toLocaleString()}.`);
      return;
    }
    setBailing(true);
    setBailError('');
    try {
      const { data, error: bailErr } = await supabase?.rpc('bail_out', { p_user_id: user?.id });
      if (bailErr) throw bailErr;
      if (data && !data?.success) throw new Error(data?.error || 'Bail failed');
      navigate('/dashboard');
    } catch (err) {
      const msg = err?.message?.toLowerCase() || '';
      if (msg?.includes('insufficient') || msg?.includes('funds') || msg?.includes('cash')) {
        setBailError('Insufficient funds to pay bail.');
      } else {
        setBailError(err?.message || 'Bail payment failed. Please try again.');
      }
    } finally {
      setBailing(false);
    }
  };

  const bailCost = parseFloat(playerStats?.bail_cost || 0);
  const evidenceLevel = parseFloat(playerStats?.evidence_level || 0);
  const jailReason = 'Caught by authorities during criminal activity';
  const totalJailSeconds = playerStats?.jail_until
    ? Math.round((new Date(playerStats?.jail_until) - new Date(Date.now() - timeRemaining * 1000)) / 1000)
    : 3600;

  if (loading) {
    return (
      <div className="flex h-screen bg-background text-foreground overflow-hidden">
        <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(p => !p)} />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto mb-3" />
            <p className="text-muted-foreground">Loading jail status...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen bg-background text-foreground overflow-hidden">
        <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(p => !p)} />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center p-6">
            <Icon name="AlertCircle" size={32} color="var(--color-error)" className="mx-auto mb-3" />
            <p className="text-error mb-4">{error}</p>
            <button onClick={fetchJailStatus} className="px-4 py-2 bg-primary text-zinc-950 rounded-md text-sm font-semibold">Retry</button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <MainSidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(p => !p)}
        jailStatus={true}
        jailTimer={timeRemaining}
        userStats={{
          level: playerStats?.level || 1,
          energy: playerStats?.energy || 0,
          maxEnergy: playerStats?.max_energy || 100,
          xp: parseFloat(playerStats?.xp || 0),
          xpToNext: parseFloat(playerStats?.xp_to_next_level || 10000),
        }}
      />
      <main className="flex-1 overflow-y-auto">
        <div className="p-6 max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-500/10 border-2 border-red-500/40 mb-4">
              <Icon name="Lock" size={40} color="#EF4444" />
            </div>
            <h1 className="text-h1 font-heading text-red-400 mb-2">YOU ARE IN JAIL</h1>
            <p className="text-muted-foreground">{jailReason}</p>
          </div>

          {/* Timer */}
          <div className="card p-8 text-center mb-6 border border-red-500/30">
            <p className="text-sm text-muted-foreground uppercase tracking-widest mb-2">Time Remaining</p>
            <div className="text-6xl font-mono font-bold text-red-400 mb-4">
              {formatTime(timeRemaining)}
            </div>
            <div className="w-full bg-surface rounded-full h-3 mb-4">
              <div
                className="h-3 rounded-full bg-red-500 transition-all duration-1000"
                style={{ width: `${totalJailSeconds > 0 ? (timeRemaining / totalJailSeconds) * 100 : 0}%` }}
              />
            </div>
            <p className="text-sm text-muted-foreground">Trading, crimes, and business operations are suspended</p>
          </div>

          {/* Penalties & Bail */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Status</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-foreground flex items-center gap-2">
                    <Icon name="AlertTriangle" size={14} color="#FFB800" /> Evidence Level
                  </span>
                  <span className="text-warning font-mono">{Math.round(evidenceLevel)}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-foreground flex items-center gap-2">
                    <Icon name="DollarSign" size={14} color="var(--color-success)" /> Cash Available
                  </span>
                  <span className="text-success font-mono">${parseFloat(playerStats?.cash || 0)?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-foreground flex items-center gap-2">
                    <Icon name="Clock" size={14} color="var(--color-primary)" /> Release Time
                  </span>
                  <span className="text-primary font-mono text-xs">
                    {playerStats?.jail_until ? new Date(playerStats?.jail_until)?.toLocaleTimeString() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div className="card p-5 border border-warning/20">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Bail Option</h3>
              <div className="text-center">
                <p className="text-3xl font-mono font-bold text-warning mb-2">${bailCost?.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mb-2">Pay bail to be released immediately</p>
                {bailError && (
                  <p className="text-xs text-error mb-3 flex items-center justify-center gap-1">
                    <Icon name="AlertCircle" size={12} color="var(--color-error)" />
                    {bailError}
                  </p>
                )}
                <button
                  className="w-full py-3 rounded-md bg-warning text-zinc-950 font-semibold hover:bg-warning/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  onClick={handleBailOut}
                  disabled={bailing || bailCost <= 0 || parseFloat(playerStats?.cash || 0) < bailCost}
                  aria-label={`Pay bail of $${bailCost?.toLocaleString()}`}
                >
                  {bailing ? (
                    <><Icon name="Loader2" size={16} className="animate-spin" /> Processing...</>
                  ) : (
                    `Pay Bail — $${bailCost?.toLocaleString()}`
                  )}
                </button>
                {parseFloat(playerStats?.cash || 0) < bailCost && bailCost > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">Insufficient funds to pay bail</p>
                )}
              </div>
            </div>
          </div>

          {/* Jail Activities */}
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Available While Jailed</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon: 'BookOpen', label: 'Study Skills', desc: '+0.1 skill/hr', path: null },
                { icon: 'Users', label: 'Recruit Gang', desc: 'Find allies', path: '/factions' },
                { icon: 'BarChart2', label: 'Watch Market', desc: 'Read only', path: '/stock-exchange' },
                { icon: 'MessageSquare', label: 'Faction Chat', desc: 'Communicate', path: '/faction-chat' },
              ]?.map((activity) => (
                <button
                  key={activity?.label}
                  onClick={() => activity?.path && navigate(activity?.path)}
                  className="p-3 rounded-md bg-surface border border-border hover:border-primary/40 transition-all text-center"
                  aria-label={activity?.label}
                >
                  <Icon name={activity?.icon} size={20} color="var(--color-primary)" className="mx-auto mb-2" />
                  <p className="text-xs font-semibold text-foreground">{activity?.label}</p>
                  <p className="text-xs text-muted-foreground">{activity?.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default JailPage;
